import { XMarkIcon } from '@heroicons/react/24/outline';
import { useQueries } from '@tanstack/react-query';
import { isArray } from 'lodash';
import { useTranslation } from 'react-i18next';

import { getCourseBase } from '@/data/services/courseServices';
import { getKlass } from '@/data/services/klassServices';
import { getUnit } from '@/data/services/unitServices';
import { getUser } from '@/data/services/userServices';
import { handleUserFullName } from '@/functions/handleUserFullName';
import { FieldValues, FilterFormProps } from '@/models/FilterConfig';
import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import IconButton from '@/components/ui/custom/buttons/IconButton';
import { TranslateKeys, useFilterLabelTranslated } from './Filter.hooks';

type Props = FilterFormProps & {
  filter?: FieldValues;
};

export default function QuickFilters(props: Props) {
  const { filter = {}, setValue } = props;

  const multipleFilters = Object.fromEntries(
    Object.entries(filter).filter(
      entry => isArray(entry[1]) && entry[1].length,
    ) as [string, Array<string | number>][],
  );

  function removeValue(key: string, value: string | number) {
    const values = filter?.[key] as Array<string | number>;

    const newValues = values.filter((v: string | number) => v !== value);

    setValue(key, newValues);
  }

  const removeKey = (key: string) => {
    setValue(key, undefined);
  };

  return (
    <ConditionalRenderer condition={Object.keys(multipleFilters).length}>
      <ul className="flex flex-col gap-1">
        {Object.entries(multipleFilters).map(([key, value]) => (
          <QuickFilter
            key={key}
            type={key as TranslateKeys}
            items={value}
            removeValue={removeValue}
            removeKey={removeKey}
          />
        ))}
      </ul>
    </ConditionalRenderer>
  );
}

type QuickFilterProps = {
  type: TranslateKeys;
  items: Array<string | number>;
  removeValue: (key: string, value: string | number) => void;
  removeKey: (key: string) => void;
};

function QuickFilter(props: QuickFilterProps) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'common',
  });

  type QueryItem = {
    label: string;
    value: string | number;
  };

  const { translate } = useFilterLabelTranslated();

  const { type, items, removeValue, removeKey } = props;

  const idToNameMapping: Record<string, (id: number) => Promise<string>> = {
    unitId: async id => await getUnit(id).then(data => data.name),
    klassId: async id => await getKlass(id).then(data => data.name),
    courseId: async id =>
      await getCourseBase(id).then(data => data.abbreviation),
    userId: async id =>
      await getUser(id).then(data => handleUserFullName(data)),
    teacherId: async id =>
      await getUser(id).then(data => handleUserFullName(data)),
  };

  const queries = useQueries({
    queries: items.map(value => ({
      queryKey: [type, value],
      queryFn: async (): Promise<QueryItem> => {
        const label =
          typeof value === 'number'
            ? await idToNameMapping[type](value)
            : translate(type, value);

        return { label, value };
      },
      placeholderData: (): QueryItem => ({
        label: `${t('loading')}...`,
        value,
      }),
    })),
  });

  return (
    <li className="flex gap-1 items-center">
      <ul className="flex flex-wrap gap-1 items-center">
        {queries.map(({ data, isInitialLoading }) => {
          if (isInitialLoading) {
            return (
              <span
                key={data?.value}
                className="h-7 w-40 bg-primary-content rounded-full"
              />
            );
          }

          if (data) {
            return (
              <li
                key={data.value}
                className="rounded-full text-primary bg-primary-content px-3 py-1 text-14 font-500 gap-2 cursor-pointer"
              >
                <button className="flex items-center gap-0.5">
                  {data.label}
                  <XMarkIcon
                    className="w-4 h-4"
                    onClick={() => removeValue(type, data.value)}
                  />
                </button>
              </li>
            );
          }

          return null;
        })}
      </ul>
      <ConditionalRenderer condition={queries.length}>
        <IconButton
          icon={<XMarkIcon className="w-4 h-4 text-error" />}
          onClick={() => removeKey(type)}
        />
      </ConditionalRenderer>
    </li>
  );
}
